import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, Typography, Chip,
  Button, Alert, Collapse, Paper, Divider, IconButton, Avatar, TextField, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Calendar, Add, ArrowDown2, ArrowUp2, Link21, Messages2, Send2 } from 'iconsax-react';
import { useTaskStore } from '../../store/task.store';
import { getGoogleStatus } from '../../services/settings.service';
import { replyToThread, getSyncStatus, syncTasksOnce } from '../../services/task.service';
import { Task, TaskFilters, ThreadMessage } from '../../types/task.type';
import { getApiErrorDetail } from '../../utils/taskApi.utils';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import {
  DueDateFilter,
  ObjectiveLinkFilter,
  TaskSortDir,
  TaskSortField,
  TaskStatusTab,
  WORK_STATUS_COLORS,
  WORK_STATUSES,
  buildToggleCompleteWrite,
  dueHintParts,
  filterTasks,
  getSortLabel,
  getStatusCounts,
  getUniqueSpaces,
  getUniqueTaskLists,
  getLinkedObjectives,
  getWorkStatus,
  sortTasks,
  taskKey,
  toggleSortDir,
  workStatusLabelKey,
} from '../../utils/task.utils';
import TasksToolbar from '../../components/tasks/TasksToolbar';
import SortableColumnHeader from '../../components/tasks/SortableColumnHeader';
import TaskTableSkeleton from '../../components/tasks/TaskTableSkeleton';
import TasksRefreshButton from '../../components/tasks/TasksRefreshButton';
import VirtualTaskRows from '../../components/tasks/VirtualTaskRows';
import TaskRowActions from '../../components/tasks/TaskRowActions';
import TaskFormDialog from '../../components/tasks/TaskFormDialog';
import DeleteTaskDialog from '../../components/tasks/DeleteTaskDialog';

const MY_GRID = '1fr minmax(220px, 1.4fr) 110px 100px minmax(108px, auto)';

function MyTaskRow({
  task,
  actionsLoading,
  onToggleComplete,
  onEdit,
  onDelete,
}: {
  task: Task;
  actionsLoading?: boolean;
  onToggleComplete: (task: Task) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
}) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [threadExpanded, setThreadExpanded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [localMessages, setLocalMessages] = useState<ThreadMessage[]>([]);
  const workStatus = getWorkStatus(task);
  const overdue = task.is_overdue && workStatus !== 'completed';
  const statusBg = WORK_STATUS_COLORS[workStatus];
  const hint = dueHintParts(task);
  const dueHint = hint ? t(hint.key, { count: hint.count }) : null;
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  const hasThread = task.thread_link && task.thread_messages && task.thread_messages.length > 0;

  // Sync local messages with task messages
  useEffect(() => {
    if (task.thread_messages) {
      setLocalMessages(task.thread_messages);
    }
  }, [task.thread_messages]);

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenInChat = () => {
    if (task.thread_link) {
      window.open(task.thread_link, '_blank');
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !task.thread_name || sending) return;
    
    setSending(true);
    try {
      const newMessage = await replyToThread({
        google_task_id: task.google_task_id,
        task_list_id: task.task_list_id,
        employee_id: task.employee_id,
        thread_name: task.thread_name,
        text: replyText.trim(),
      });
      setLocalMessages(prev => [...prev, newMessage]);
      setReplyText('');
      showToast(t('tasks.thread.replySent') || 'Reply sent', TOAST_TYPES.SUCCESS);
    } catch (error) {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: MY_GRID,
          alignItems: 'center', px: 2.5, py: 1.5, gap: 2,
          borderBottom: hasThread && !threadExpanded ? '1px solid #F3F4F6' : 'none',
          transition: 'background 0.12s',
          '&:hover': { bgcolor: '#F9FAFB' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {task.title}
            </Typography>
            {hasThread && (
              <IconButton 
                size="small" 
                onClick={() => setThreadExpanded(!threadExpanded)}
                sx={{ p: 0.25, ml: 0.5 }}
              >
                {threadExpanded ? <ArrowUp2 size={14} color="#3B82F6" /> : <ArrowDown2 size={14} color="#3B82F6" />}
              </IconButton>
            )}
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
            {task.task_list_name}
          </Typography>
          {(task.linked_objectives?.length ?? 0) > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {task.linked_objectives.map(o => (
                <Chip
                  key={o.id}
                  icon={<Link21 size={11} color="#4338CA" />}
                  label={o.title}
                  size="small"
                  sx={{
                    height: 20,
                    maxWidth: '100%',
                    bgcolor: '#EEF2FF',
                    color: '#4338CA',
                    border: '1px solid #C7D2FE',
                    borderRadius: '6px',
                    '& .MuiChip-icon': { ml: '6px', mr: '-2px' },
                    '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {task.space_name || '—'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Calendar size={13} color={overdue ? '#EF4444' : '#94A3B8'} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.8125rem', color: overdue ? '#EF4444' : 'text.secondary', fontWeight: overdue ? 600 : 400 }}>
              {task.due ? new Date(task.due).toLocaleDateString(locale, { month: 'short', day: 'numeric' }) : '—'}
            </Typography>
            {dueHint && (
              <Typography sx={{ fontSize: '0.7rem', color: overdue ? '#EF4444' : 'text.secondary', lineHeight: 1.2 }}>
                {dueHint}
              </Typography>
            )}
          </Box>
        </Box>
        <Chip
          label={t(workStatusLabelKey(workStatus))}
          size="small"
          sx={{ bgcolor: statusBg.bg, color: statusBg.color, fontWeight: 500, fontSize: '0.75rem', border: 'none', height: 24 }}
        />

        <TaskRowActions
          task={task}
          loading={actionsLoading}
          onToggleComplete={onToggleComplete}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </Box>

      {hasThread && (
        <Collapse in={threadExpanded}>
          <Box sx={{ px: 2.5, pb: 1.5, borderBottom: '1px solid #F3F4F6' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 1,
              pb: 1,
              borderBottom: '1px solid #F3F4F6'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Messages2 size={16} color="#3B82F6" />
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151' }}>
                  {t('tasks.thread.title')}
                </Typography>
                <Chip
                  label={localMessages.length}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: '#DBEAFE',
                    color: '#2563EB',
                    border: 'none',
                  }}
                />
              </Box>
              <IconButton
                size="small"
                onClick={handleOpenInChat}
                sx={{ p: 0.5 }}
              >
                <Link21 size={16} color="#3B82F6" />
              </IconButton>
            </Box>
            
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: '#FAFBFC',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                maxHeight: '300px',
                overflowY: 'auto',
                mb: 1.5,
              }}
            >
              {localMessages.map((msg, index) => (
                <Box key={msg.message_id}>
                  {index > 0 && <Divider sx={{ my: 1.5 }} />}
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Avatar
                      src={msg.sender_avatar_url || undefined}
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        bgcolor: msg.sender_type === 'BOT' ? '#EEF2FF' : '#DBEAFE',
                        color: msg.sender_type === 'BOT' ? '#6366F1' : '#2563EB',
                      }}
                    >
                      {!msg.sender_avatar_url && msg.sender_name.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#111827' }}>
                          {msg.sender_name}
                        </Typography>
                        {msg.sender_type === 'BOT' && (
                          <Chip
                            label="BOT"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: '#DBEAFE',
                              color: '#2563EB',
                              border: 'none',
                            }}
                          />
                        )}
                        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                          {formatTime(msg.create_time)}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          color: '#374151',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg.text}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>

            {/* Reply Input */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={3}
                placeholder={t('tasks.thread.replyPlaceholder') || 'اكتب رداً...'}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                  },
                }}
              />
              <IconButton
                onClick={handleSendReply}
                disabled={!replyText.trim() || sending}
                sx={{
                  bgcolor: '#3B82F6',
                  color: '#fff',
                  '&:hover': { bgcolor: '#2563EB' },
                  '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                }}
              >
                {sending ? <CircularProgress size={20} color="inherit" /> : <Send2 size={20} color="#fff" variant="Bold" />}
              </IconButton>
            </Box>
          </Box>
        </Collapse>
      )}
    </>
  );
}

const DUE_FILTER_LABELS: Record<DueDateFilter, string> = {
  '': '',
  overdue: 'tasks.dueFilters.overdue',
  thisWeek: 'tasks.dueFilters.thisWeek',
  noDue: 'tasks.dueFilters.noDue',
};

export default function MyTasksPage() {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const {
    myTasks, myTasksLoading, myTasksRefreshing, myTasksError, fetchMyTasks, syncAndRefreshMyTasks,
    createManagerTask, updateManagerTask, deleteManagerTask,
  } = useTaskStore();
  const [filters, setFilters] = useState<TaskFilters & { dueDateFilter?: DueDateFilter; objectiveLink?: ObjectiveLinkFilter; objectiveId?: string }>({});
  const [activeTab, setActiveTab] = useState<TaskStatusTab>('all');
  const [googleConnected, setGoogleConnected] = useState<boolean | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [sortField, setSortField] = useState<TaskSortField>('due');
  const [sortDir, setSortDir] = useState<TaskSortDir>('asc');
  const [formOpen, setFormOpen] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchedAt = useTaskStore.getState().myTasksFetchedAt;
    if (!fetchedAt || Date.now() - fetchedAt > 20000) fetchMyTasks();

    (async () => {
      try {
        const res: any = await getGoogleStatus();
        if (cancelled) return;
        const connected = res.connected ?? false;
        setGoogleConnected(connected);
        if (!connected) return;

        const status = await getSyncStatus();
        if (cancelled) return;
        setLastSyncedAt(status.last_synced_at);
        if (status.last_synced_at != null) return;

        setSyncing(true);
        try {
          await syncTasksOnce();
          if (cancelled) return;
          const refreshed = await getSyncStatus();
          if (!cancelled) setLastSyncedAt(refreshed.last_synced_at);
          await fetchMyTasks();
        } catch (e: any) {
          if (cancelled) return;
          if (e?.response?.status === 403) {
            setGoogleConnected(false);
            return;
          }
          showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
        } finally {
          if (!cancelled) setSyncing(false);
        }
      } catch {
        if (!cancelled) setGoogleConnected(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fetchMyTasks, showToast, t]);

  const handleRefresh = async () => {
    try {
      await syncAndRefreshMyTasks({});
      const status = await getSyncStatus();
      setLastSyncedAt(status.last_synced_at);
    } catch (e: any) {
      if (e?.response?.status === 403) {
        setGoogleConnected(false);
        return;
      }
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    }
  };

  const spaces = useMemo(() => getUniqueSpaces(myTasks), [myTasks]);
  const taskLists = useMemo(() => getUniqueTaskLists(myTasks), [myTasks]);
  const linkedObjectives = useMemo(() => getLinkedObjectives(myTasks), [myTasks]);

  const filtered = useMemo(() => {
    const result = filterTasks(myTasks, {
      search: filters.search,
      space_name: filters.space_name,
      task_list_id: filters.task_list_id,
      dueDateFilter: filters.dueDateFilter,
      objectiveLink: filters.objectiveLink,
      objectiveId: filters.objectiveLink === 'linked' ? filters.objectiveId : '',
      statusTab: activeTab,
    });
    return sortTasks(result, sortField, sortDir);
  }, [myTasks, filters, activeTab, sortField, sortDir]);

  const counts = useMemo(() => getStatusCounts(myTasks), [myTasks]);

  const statusTabs = [
    { key: 'all' as const, label: t('tasks.statusAll'), count: counts.all },
    ...WORK_STATUSES.map(status => ({
      key: status,
      label: t(workStatusLabelKey(status)),
      count: counts[status],
    })),
  ];

  const handleSort = (field: TaskSortField) => {
    setSortDir(toggleSortDir(sortField, sortDir, field));
    setSortField(field);
  };

  const handleDueSortToggle = () => {
    setSortField('due');
    setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.space_name) {
      chips.push({
        key: 'space',
        label: `${t('tasks.space')}: ${filters.space_name}`,
        onRemove: () => setFilters(f => ({ ...f, space_name: '' })),
      });
    }
    if (filters.task_list_id) {
      const list = taskLists.find(l => l.id === filters.task_list_id);
      chips.push({
        key: 'list',
        label: `${t('tasks.googleList')}: ${list?.name ?? ''}`,
        onRemove: () => setFilters(f => ({ ...f, task_list_id: '' })),
      });
    }
    if (filters.dueDateFilter) {
      chips.push({
        key: 'due',
        label: t(DUE_FILTER_LABELS[filters.dueDateFilter]),
        onRemove: () => setFilters(f => ({ ...f, dueDateFilter: '' })),
      });
    }
    if (filters.objectiveLink) {
      chips.push({
        key: 'objective',
        label: filters.objectiveLink === 'linked' ? t('tasks.linked') : t('tasks.notLinked'),
        onRemove: () => setFilters(f => ({ ...f, objectiveLink: '', objectiveId: '' })),
      });
    }
    if (filters.objectiveLink === 'linked' && filters.objectiveId) {
      const objective = linkedObjectives.find(o => o.id === filters.objectiveId);
      chips.push({
        key: 'objectiveId',
        label: `${t('tasks.linkedObjective')}: ${objective?.title ?? ''}`,
        onRemove: () => setFilters(f => ({ ...f, objectiveId: '' })),
      });
    }
    return chips;
  }, [filters, taskLists, linkedObjectives, t]);

  const filterGroups = useMemo(() => [
    {
      key: 'space',
      label: t('tasks.space'),
      value: filters.space_name || '',
      allLabel: t('tasks.allSpaces'),
      options: spaces.map(name => ({ id: name, label: name })),
      onChange: (v: string) => setFilters(f => ({ ...f, space_name: v })),
    },
    {
      key: 'list',
      label: t('tasks.googleList'),
      value: filters.task_list_id || '',
      allLabel: t('tasks.allTaskLists'),
      options: taskLists.map(l => ({ id: l.id, label: l.name })),
      onChange: (v: string) => setFilters(f => ({ ...f, task_list_id: v })),
      hidden: taskLists.length <= 1,
    },
    {
      key: 'due',
      label: t('tasks.dueDateFilter'),
      value: filters.dueDateFilter || '',
      allLabel: t('tasks.dueFilters.all'),
      options: [
        { id: 'overdue', label: t('tasks.dueFilters.overdue') },
        { id: 'thisWeek', label: t('tasks.dueFilters.thisWeek') },
        { id: 'noDue', label: t('tasks.dueFilters.noDue') },
      ],
      onChange: (v: string) => setFilters(f => ({ ...f, dueDateFilter: v as DueDateFilter })),
    },
    {
      key: 'objective',
      label: t('tasks.objectiveFilter'),
      value: filters.objectiveLink || '',
      allLabel: t('tasks.allTasks'),
      options: [
        { id: 'linked', label: t('tasks.linked') },
        { id: 'unlinked', label: t('tasks.notLinked') },
      ],
      onChange: (v: string) => setFilters(f => ({
        ...f,
        objectiveLink: v as ObjectiveLinkFilter,
        objectiveId: v === 'linked' ? f.objectiveId : '',
      })),
    },
    {
      key: 'objectiveId',
      label: t('tasks.linkedObjective'),
      value: filters.objectiveId || '',
      allLabel: t('tasks.allObjectives'),
      options: linkedObjectives.map(o => ({ id: o.id, label: o.title })),
      onChange: (v: string) => setFilters(f => ({ ...f, objectiveId: v })),
      hidden: filters.objectiveLink !== 'linked',
    },
  ], [filters, spaces, taskLists, linkedObjectives, t]);

  const columns: { label: string; field: TaskSortField }[] = [
    { label: t('tasks.tableTask'), field: 'title' },
    { label: t('tasks.tableSpace'), field: 'space' },
    { label: t('tasks.tableDueDate'), field: 'due' },
    { label: t('tasks.tableStatus'), field: 'status' },
  ];

  const showGoogleRequired = googleConnected === false || myTasksError === 'Manager must connect Google account to view My Tasks';

  const handleToggleComplete = async (task: Task) => {
    setMutatingId(task.google_task_id);
    try {
      await updateManagerTask(task.google_task_id, {
        task_list_id: task.task_list_id,
        ...buildToggleCompleteWrite(task),
      });
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setMutatingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteManagerTask(deleteTarget);
      showToast(t('tasks.taskDeleted'), TOAST_TYPES.SUCCESS);
      setDeleteTarget(null);
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px' }}>
            {t('tasks.myTasksTitle')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5 }}>
            {t('tasks.myTasksSubtitle')}
          </Typography>
        </Box>
        {googleConnected && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add size={16} color="#fff" />}
              onClick={() => { setEditTask(null); setFormOpen(true); }}
              sx={{ textTransform: 'none' }}
            >
              {t('tasks.addTask')}
            </Button>
            <TasksRefreshButton onClick={handleRefresh} refreshing={myTasksRefreshing} />
            {lastSyncedAt && (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {t('tasks.lastSynced')}: {new Date(lastSyncedAt).toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {showGoogleRequired ? (
        <Alert
          severity="warning"
          sx={{ mb: 2.5, borderRadius: 2 }}
          action={
            <Button component={RouterLink} to="/dashboard/settings" size="small" color="inherit">
              {t('tasks.connectGoogle')}
            </Button>
          }
        >
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>
            {t('tasks.googleRequiredTitle')}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem' }}>
            {t('tasks.googleRequiredDesc')}
          </Typography>
        </Alert>
      ) : (
        <>
          <TasksToolbar
            searchValue={filters.search || ''}
            searchPlaceholder={t('tasks.myTasksSearchPlaceholder')}
            onSearchChange={v => setFilters(f => ({ ...f, search: v }))}
            statusTabs={statusTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            filterGroups={filterGroups}
            activeChips={activeChips}
            onClearAll={() => setFilters({})}
            sortLabel={getSortLabel(sortField, sortDir, t)}
            onSortToggle={handleDueSortToggle}
            filteredCount={filtered.length}
            totalCount={myTasks.length}
          />

          {myTasksLoading && myTasks.length === 0 ? (
            <TaskTableSkeleton variant="my" rows={10} />
          ) : myTasksError && !showGoogleRequired ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{myTasksError}</Alert>
          ) : (
            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: MY_GRID, px: 2.5, py: 1.25, gap: 2, borderBottom: '1px solid #F3F4F6', bgcolor: '#F9FAFB' }}>
                {columns.map(col => (
                  <SortableColumnHeader
                    key={col.field}
                    label={col.label}
                    field={col.field}
                    activeField={sortField}
                    activeDir={sortDir}
                    onSort={handleSort}
                  />
                ))}
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textAlign: 'end' }}>
                  {t('common.actions')}
                </Typography>
              </Box>
              {filtered.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {syncing ? t('tasks.syncing') : t('tasks.noTasks')}
                  </Typography>
                </Box>
              ) : (
                <VirtualTaskRows
                  items={filtered}
                  getKey={taskKey}
                  renderItem={task => (
                    <MyTaskRow
                      task={task}
                      actionsLoading={mutatingId === task.google_task_id}
                      onToggleComplete={handleToggleComplete}
                      onEdit={t => { setEditTask(t); setFormOpen(true); }}
                      onDelete={setDeleteTarget}
                    />
                  )}
                />
              )}
            </Card>
          )}
        </>
      )}

      <TaskFormDialog
        open={formOpen}
        mode="my"
        task={editTask}
        onClose={() => { setFormOpen(false); setEditTask(null); }}
        onSubmitEmployee={async () => {}}
        onSubmitMy={async (payload) => {
          await createManagerTask(payload);
          showToast(t('tasks.taskCreated'), TOAST_TYPES.SUCCESS);
        }}
        onSubmitEditEmployee={async () => {}}
        onSubmitEditMy={async (payload) => {
          if (!editTask) return;
          await updateManagerTask(editTask.google_task_id, payload);
          showToast(t('tasks.taskUpdated'), TOAST_TYPES.SUCCESS);
        }}
      />

      <DeleteTaskDialog
        open={!!deleteTarget}
        task={deleteTarget}
        loading={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
